import {
  addDays,
  format,
  isBefore,
  isSameDay,
  parseISO,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { useRef } from "react";
import useSWR from "swr";

import { BookingType } from "../../data";
import { toNumber } from "../../lib/js_utils/number_utils";
import { DayPassDateRange } from "../../offering/order_params";
import { hasSmartPricingSearchParam } from "../utils/has_smart_pricing_search_param";

export function useBestDayRatesQuery(
  range: { startDate?: Date; endDate?: Date },
  bookingType: BookingType,
  spaceId?: string,
  spaceLayoutId?: string
) {
  const params = new URLSearchParams();
  params.set("booking_type", bookingType);
  if (spaceLayoutId) {
    params.set("space_layout_id", spaceLayoutId);
  }
  if (hasSmartPricingSearchParam()) {
    params.set("smart_pricing", "true");
  }
  const datesParam = getDatesInBetween(range.startDate, range.endDate);
  const queryString = params.toString().concat(`&${datesParam.join("&")}`);

  const response = useSWR(
    datesParam && spaceId
      ? `/api/v2/spaces/${spaceId}/dynamic_rates/best_day_rates?${queryString}`
      : null
  );

  // This SWR version has no keepPreviousData: a key change (month navigation,
  // booking-type switch) returns data to undefined while the new request is in
  // flight. Serving the last settled payload keeps values derived from options
  // — the better-hour-rates price seed, the selected date's price — stable
  // instead of tearing them down and re-firing on every navigation.
  const lastSettled = useRef<unknown>();
  if (response.data !== undefined) {
    lastSettled.current = response.data;
  }
  const settledData = response.data ?? lastSettled.current;

  return {
    error: response.error,
    // True only until the FIRST response settles. Consumers that derive other
    // request keys from options (e.g. the better-hour-rates price seed) must
    // wait for it, or they fire once with a fallback value and again when
    // options land.
    isLoading: settledData === undefined && !response.error,
    data: toBestDayRates(settledData),
  };
}

export function getViewRange(
  viewingRange?: { startDate: Date; endDate: Date },
  internalValue?: DayPassDateRange
): {
  startDate: Date;
  endDate: Date;
} {
  if (viewingRange) {
    return viewingRange;
  }
  // One month only. A selection spanning two calendar months used to widen to
  // startOfMonth(start)..endOfMonth(end) — up to 61 dates against a server that
  // caps at 31, so the tail came back empty. That was invisible while the client
  // filled gaps from a base price; now an unpriced date renders blank. Month
  // navigation already re-requests a single month at a time.
  if (internalValue?.startDate) {
    const start = parseISO(internalValue.startDate);
    return { startDate: startOfMonth(start), endDate: endOfMonth(start) };
  }
  return {
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
  };
}

export interface DayRates {
  [date: string]: number;
}

interface BestDayDates {
  bookingType?: "daily" | "hourly";
  // The rack rate the backend resolved for this space, layout and booking type.
  // It is the only base the client may compare against: it already accounts for
  // `use_layout_prices`, day-of-week overrides and pricing time bands, none of
  // which the space payload exposes.
  originalUnitPrice?: number;
  options: DayRates;
}

function toBestDayRates(data: unknown): BestDayDates {
  const payload = data as
    | {
        booking_type?: "daily" | "hourly";
        original_unit_price?: unknown;
        options?: DayRates;
      }
    | undefined;

  const originalUnitPrice = toNumber(payload?.original_unit_price);

  return {
    bookingType: payload?.booking_type,
    originalUnitPrice: Number.isNaN(originalUnitPrice)
      ? undefined
      : originalUnitPrice,
    options: { ...payload?.options },
  };
}

function getDatesInBetween(startDate?: Date, endDate?: Date) {
  if (!startDate || !endDate) {
    return [];
  }

  const datesParam = [];
  for (
    let i = 0;
    isBefore(addDays(startDate, i), endDate) ||
    isSameDay(addDays(startDate, i), endDate);
    i++
  ) {
    const checkDate = addDays(startDate, i);
    datesParam.push(`dates[]=${format(checkDate, "yyyy-MM-dd")}`);
  }

  return datesParam;
}
